import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Link,
  Copy,
  Check,
  Trash2,
  Eye,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useShareByResource,
  useCreateShare,
  useUpdateShare,
  useDeleteShare,
  generateShareUrl,
} from '@/hooks';
import type { ShareLink } from '@/types/database';

interface ShareDialogProps {
  resourceType: 'collection' | 'bookmark';
  resourceId: string;
  resourceName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShareDialog({
  resourceType,
  resourceId,
  resourceName,
  open,
  onOpenChange,
}: ShareDialogProps) {
  const [copied, setCopied] = useState(false);
  const [slug, setSlug] = useState('');
  const [allowCopy, setAllowCopy] = useState(true);

  // Queries
  const { data: shareStatus, isLoading } = useShareByResource(resourceType, resourceId);
  const createShare = useCreateShare();
  const updateShare = useUpdateShare();
  const deleteShare = useDeleteShare();

  const existingShare = shareStatus?.shared ? shareStatus.share : null;

  // Update local state when share data loads
  useEffect(() => {
    if (existingShare) {
      setSlug(existingShare.slug || '');
      setAllowCopy(existingShare.allow_copy);
    } else {
      setSlug('');
      setAllowCopy(true);
    }
  }, [existingShare]);

  const handleCreateShare = async () => {
    try {
      await createShare.mutateAsync({
        resource_type: resourceType,
        resource_id: resourceId,
        slug: slug || undefined,
        allow_copy: allowCopy,
      });
      toast.success('공유 링크가 생성되었습니다');
    } catch {
      toast.error('공유 링크 생성에 실패했습니다');
    }
  };

  const handleUpdateShare = async () => {
    if (!existingShare) return;
    try {
      await updateShare.mutateAsync({
        id: existingShare.id,
        updates: {
          slug: slug || null,
          allow_copy: allowCopy,
        },
      });
      toast.success('공유 설정이 업데이트되었습니다');
    } catch {
      toast.error('공유 설정 업데이트에 실패했습니다');
    }
  };

  const handleDeleteShare = async () => {
    if (!existingShare) return;
    try {
      await deleteShare.mutateAsync(existingShare.id);
      toast.success('공유가 중지되었습니다');
    } catch {
      toast.error('공유 중지에 실패했습니다');
    }
  };

  const handleCopyLink = async () => {
    if (!existingShare) return;
    const url = generateShareUrl(existingShare);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('링크가 복사되었습니다');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('링크 복사에 실패했습니다');
    }
  };

  const isPending = createShare.isPending || updateShare.isPending || deleteShare.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link className="w-5 h-5" />
            공유하기
          </DialogTitle>
          <DialogDescription>
            {resourceType === 'collection' ? '컬렉션' : '북마크'}을 다른 사람과 공유합니다
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : existingShare ? (
          // Already shared - show settings
          <div className="space-y-4">
            {/* Share URL */}
            <div className="space-y-2">
              <Label>공유 링크</Label>
              <div className="flex gap-2">
                <Input
                  value={generateShareUrl(existingShare)}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopyLink}
                  disabled={isPending}
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>

            {/* Custom slug */}
            <div className="space-y-2">
              <Label htmlFor="slug">커스텀 URL (선택)</Label>
              <Input
                id="slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value.replace(/[^a-z0-9-]/g, ''))}
                placeholder="my-collection"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                영문 소문자, 숫자, 하이픈만 사용 가능
              </p>
            </div>

            {/* Allow copy option */}
            {resourceType === 'collection' && (
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>북마크 복사 허용</Label>
                  <p className="text-xs text-muted-foreground">
                    방문자가 북마크를 자신의 계정으로 복사할 수 있습니다
                  </p>
                </div>
                <Switch
                  checked={allowCopy}
                  onCheckedChange={setAllowCopy}
                />
              </div>
            )}

            {/* Stats */}
            <div className="flex items-center gap-4 text-sm text-muted-foreground pt-2">
              <div className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                <span>조회 {existingShare.view_count}회</span>
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                variant="destructive"
                onClick={handleDeleteShare}
                disabled={isPending}
                className="w-full sm:w-auto"
              >
                {deleteShare.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                공유 중지
              </Button>
              <Button
                onClick={handleUpdateShare}
                disabled={isPending}
                className="w-full sm:w-auto"
              >
                {updateShare.isPending && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                설정 저장
              </Button>
            </DialogFooter>
          </div>
        ) : (
          // Not shared - show create form
          <div className="space-y-4">
            <div className="rounded-lg border p-4 bg-muted/50">
              <p className="text-sm font-medium">{resourceName}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {resourceType === 'collection'
                  ? '이 컬렉션의 모든 북마크가 공개됩니다'
                  : '이 북마크가 공개됩니다'}
              </p>
            </div>

            {/* Custom slug */}
            <div className="space-y-2">
              <Label htmlFor="slug">커스텀 URL (선택)</Label>
              <Input
                id="slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value.replace(/[^a-z0-9-]/g, ''))}
                placeholder="my-collection"
                className="font-mono"
              />
            </div>

            {/* Allow copy option */}
            {resourceType === 'collection' && (
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>북마크 복사 허용</Label>
                  <p className="text-xs text-muted-foreground">
                    방문자가 북마크를 복사할 수 있습니다
                  </p>
                </div>
                <Switch
                  checked={allowCopy}
                  onCheckedChange={setAllowCopy}
                />
              </div>
            )}

            <DialogFooter>
              <Button
                onClick={handleCreateShare}
                disabled={isPending}
                className="w-full"
              >
                {createShare.isPending && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                <Link className="w-4 h-4 mr-2" />
                공유 링크 만들기
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
